import { useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import jwt_decode from "jwt-decode"
import { withTranslation } from "react-i18next";
import isEmail from "validator/lib/isEmail";

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { isEqual } from "lodash";
import i18next from "@plannel/utils/i18n";

import authService from "@plannel/services/system/auth-service";
import ssoService from "@plannel/services/system/sso-service";
import PlannelFooter from "@plannel/components/PlannelFooter";
import { RouteList } from "@plannel/routeList";
import GoogleSSOSignIn from '@plannel/components/GoogleSSOSignIn';
import SelectLanguage from "@plannel/components/SelectLanguage"
import logo from "@plannel/assets/icons/main-logo.png";
import CodeUtil from "@plannel/pages/utils/code-util";
import systemService from "@plannel/services/system/system-service";
import debug from "@plannel/utils/debug-util";

const TENANT = "tenant";
const USERNAME = "username";
const PASSWORD = "password";
const EMAIL = "email";
const Signin = ({ t }) => {
  const settingsData = JSON.parse(localStorage.settings);
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: settingsData.username, tenant: settingsData.tenant });
  const [errors, setErrors] = useState({});
  const [rmCheck, setRmCheck] = useState(settingsData.rmCheck);
  const { GOOGLE, ID_PSWD } = CodeUtil.loginType;

  const setField = (field, value) => {
    setForm({
      ...form,
      [field]: value
    });

    const checkError = Boolean(errors[field]);

    if (checkError) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  const handleResponse = (response, loginUser, ssoType) => {
    const { navigateMFA } = response;
    if (navigateMFA) {
      navigate(RouteList.TwoFactorAuthentication.path, { state: { ...form, username: loginUser, ssoType } });
    } else {
      settingsData.rmCheck = rmCheck;
      if (rmCheck) {
        settingsData.username = form.username;
        settingsData.tenant = form.tenant;
      } else {
        settingsData.username = "";
        settingsData.tenant = "";
      }

      localStorage.setItem("settings", JSON.stringify(settingsData));

      if (location.pathname !== RouteList.Signin.path && location.pathname !== RouteList.Root.path) {
        navigate(location.pathname);
      } else {
        navigate(RouteList.Home.path);
      }
      window.location.reload();
    }
  }

  const handleErrorResponse = (error, loginUser, ssoType) => {
    const { data } = error.response;
    const errorMessage = data?.message ? data.message : "MSG_NetworkError";

    if (isEqual(errorMessage, "MSG_PasswordExpired")) {
      navigate(RouteList.ChangePassword.path, { state: { ...form, username: loginUser, ssoType } });
    } else {
      const password = isEqual(errorMessage, "MSG_NotMatchPassword") ? "" : form.password;
      setForm({
        ...form,
        ...data,
        password,
        errorUsername: loginUser,
        message: errorMessage
      });
    }
  }

  const onGoogleSignIn = res => {
    const { credential } = res;
    const responsePayload = jwt_decode(credential);

    const ssoInfo = {
      name: responsePayload.name,
      email: responsePayload.email,
      passedMfa: false
    }

    const loginUser = responsePayload.email;
    ssoService.googleSignInSSO(form.tenant, { ...ssoInfo })
      .then((response) => {
        handleResponse(response, loginUser, GOOGLE);
      })
      .catch(error => {
        handleErrorResponse(error, loginUser, GOOGLE);
      });
  };

  const findFormErrors = () => {
    const { username, password, tenant } = form;

    const newErrors = {};
    if (!username || username === '') {
      newErrors.username = "MSG_FieldRequired";
    } else if (!isEmail(username)) {
      newErrors.username = "MSG_CheckUsername";
    }

    if (!tenant || tenant === '') {
      newErrors.tenant = "MSG_FieldRequired";
    } else if(errors?.tenant) {
      newErrors.tenant = errors.tenant;
    }

    if (!password || password === '') {
      newErrors.password = "MSG_FieldRequired";
    }

    return newErrors
  }

  const handleLogin = (e) => {
    e.preventDefault();

    const newErrors = findFormErrors();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      const loginUser = form.username;
      authService.login(form.tenant, form.username, form.password).then(
        (response) => {
          handleResponse(response, loginUser, ID_PSWD);
        },
        (error) => {
          handleErrorResponse(error, loginUser, ID_PSWD);
        }
      );
    }
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const onBlurTenant = (e) => {
    if (e.target.value.length > 0) {
      systemService.verifyTenant(e.target.value)
      .then((response) => {
        const results = response.data;
        if (results?.message) {
          const returnErrors = { ...errors, tenant: results.message }
          setErrors(returnErrors);
        } else {
          setErrors({ ...errors, tenant: null });
        }
      })
      .catch(error => {
        debug(error);
        const { message } = error.response.data;
        setErrors({ ...errors, tenant: message });
      })
    }
  }

  const onChangeTenant = (e) => {
    const tenant = e.target.value;
    setField(TENANT, tenant);
  };

  const onChangeUsername = (e) => {
    const username = e.target.value;
    setField(USERNAME, username);
  };

  const onChangePassword = (e) => {
    const password = e.target.value;
    setField(PASSWORD, password);
  };

  const onChangeRmCheck = (e) => {
    setRmCheck(e.target.checked);
  }

  const handleNeedHelp = () => {
    navigate(RouteList.HelpSignin.path);
  }

  const handleMailAuthentication = () => {
    navigate(RouteList.MailAuthentication.path, { state: { modifiable: true } });
  }

  const setErrorMessage = () => {
    if (i18next.exists(form.message)) {
      if (isEqual(form.message, "MSG_NotMatchPassword")) {
        return t(form.message, { N: form.count });
      } else if (isEqual(form.message, "MSG_NotFoundUser")) {
        return t(form.message, { username: form.errorUsername });
      }

      return t(form.message);
    }

    return t("MSG_NetworkError");
  }

  return (
    <Container id={"signin"} component="main" maxWidth="xs">
      <CssBaseline />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }} >
        <Box mt={4}>
          <img src={logo} width={170} alt="loading..." />
        </Box>
        <Typography component="h1" variant="h5">{t("signIn")}</Typography>
        <Box component="form" onSubmit={handleLogin} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id={TENANT}
            label={t(TENANT)}
            name={TENANT}
            autoComplete={TENANT}
            onChange={onChangeTenant}
            value={form?.tenant ? form.tenant : ""}
            error={Boolean(errors.tenant)}
            helperText={t(errors.tenant)}
            onBlur={onBlurTenant}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id={EMAIL}
            label={t("emailAddress")}
            name={EMAIL}
            autoComplete={EMAIL}
            onChange={onChangeUsername}
            value={form?.username ? form.username : ""}
            error={Boolean(errors.username)}
            helperText={t(errors.username)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name={PASSWORD}
            label={t(PASSWORD)}
            type={form.showPassword ? 'text' : 'password'}
            id={PASSWORD}
            autoComplete="current-password"
            onChange={onChangePassword}
            value={form?.password ? form.password : ""}
            error={Boolean(errors.password)}
            helperText={t(errors.password)}
            InputProps={{
              endAdornment:
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setField("showPassword", !form.showPassword)}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {form.showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
            }}
          />
          <FormControlLabel
            control={<Checkbox color="primary" checked={rmCheck} onChange={onChangeRmCheck} />}
            label={t("rememberMe")}
          />
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 2 }}>{t("signIn")}</Button>
          {Boolean(form.message) && (
            <Alert sx={{ mb: 2 }} severity="error">{setErrorMessage()}</Alert>
          )}
          <Box sx={{width: "100%", height: "100%"}}>
          <GoogleSSOSignIn id="googleSSO" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} onGoogleSignIn={onGoogleSignIn} lang={i18next.language} />
          </Box>
          <Box style={{ theme: 'outline', size: 'large', shape: 'rectangular', text: 'signin_with', width: '400' }}></Box>
          <Box sx={{textAlign : "center", pt: 2, fontSize: "small"}}>
            {t("noAccount")}
            <Typography className="link-input" onClick={handleMailAuthentication} sx={{fontWeight: 'bold', pl: '5px'}} variant="body2" display={"inline"}>
              {t("signUp")}
            </Typography>
          </Box>
          <Box></Box>
          <Box sx={{ mt: 1, display: "flex" }}>
            <SelectLanguage />
            <Box sx={{ mt: 1.5, ml: "auto", mr: 1.5 }}>
              <Typography className="link-input" variant="body2" onClick={handleNeedHelp}>{t("needHelp")}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>   
      <PlannelFooter/>
    </Container>
  );
};

export default withTranslation()(Signin);